/**
 * The signed-out chrome: brand backdrop, theme and language toggles, the lockup, and one
 * card. Login was the only tenant until the password-reset screens arrived (12/10/2026);
 * the three share this so a change to the backdrop is made once.
 */

import * as React from 'react';
import { Globe, Moon, Sun } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrionLockup } from '@/components/brand/orion-logo';
import { LOCALES, useI18n } from '@/lib/i18n';
import { usePreferences } from '@/lib/preferences';

interface AuthScreenProps {
  /** Card contents — a form, or a message. */
  children: React.ReactNode;
  /** Rendered under the card, outside it; the login page's demo-account list lives here. */
  footer?: React.ReactNode;
}

export function AuthScreen({ children, footer }: AuthScreenProps) {
  const { t, locale, setLocale } = useI18n();
  const { resolvedTheme, setTheme } = usePreferences();

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden bg-[color:var(--brand-ink)] px-4 py-10">
      {/*
        Brand imagery, §06: abstract, spatial, no stock photography. Built from
        gradients rather than an image file so it costs nothing, scales to any
        viewport, and survives offline with the rest of the shell.
      */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* the horizon glow — the brand's "planet edge" */}
        <div className="absolute -bottom-[38rem] left-1/2 size-[68rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_50%_0%,rgba(78,141,255,0.34),rgba(114,102,255,0.14)_42%,transparent_68%)]" />
        {/* an off-axis violet bloom, so the field is not symmetrical */}
        <div className="absolute -top-40 right-[-10rem] size-[34rem] rounded-full bg-[radial-gradient(circle,rgba(114,102,255,0.28),transparent_66%)] blur-2xl" />
        {/* a faint constellation grid — data, not decoration */}
        <div className="absolute inset-0 opacity-[0.16] [background-image:radial-gradient(circle,rgba(255,255,255,0.85)_1px,transparent_1px)] [background-size:34px_34px]" />
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-1 text-white/80">
        <Button
          variant="ghost"
          size="icon-sm"
          className="hover:bg-white/10 hover:text-white"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          aria-label={t('settings.theme')}
        >
          {resolvedTheme === 'dark' ? <Moon /> : <Sun />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-white/10 hover:text-white"
          onClick={() => setLocale(LOCALES[(LOCALES.indexOf(locale) + 1) % LOCALES.length]!)}
        >
          <Globe /> {locale.toUpperCase()}
        </Button>
      </div>

      <div className="relative w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-4 text-center">
          <OrionLockup size="lg" />
          <p className="font-display text-sm tracking-wide text-white/55">{t('auth.brandLine')}</p>
        </div>

        <Card className="border-white/10 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-xl">
          {children}
        </Card>

        {footer}
      </div>
    </div>
  );
}

/** The input styling the signed-out forms share — white-on-ink, like the card they sit in. */
export const authInputClassName = 'border-white/15 bg-white/10 text-white placeholder:text-white/40';

/** The one error style these screens show — role=alert so a screen reader hears it. */
export function AuthError({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-[color:var(--status-critical)]/40 bg-[color:var(--status-critical)]/15 px-3 py-2 text-[13px] text-[color:var(--status-critical)]"
    >
      {children}
    </p>
  );
}
